import { HTMLAttributes } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Logo from '@/components/base/logo'
import Socials from '@/components/base/socials'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { menu, strings } = useStore()
  const { brand, links, infos, footer } = strings || {}

  return (
    <footer
      className={cn('bg-dark py-[6rem] text-center text-light', className)}
      {...props}
    >
      <Container>
        <Grid>
          <div
            className={cn(
              'col-span-4 md:col-span-6 lg:col-span-12',
              'grid items-center justify-center gap-y-[3.2rem]'
            )}
          >
            <div className="flex">
              <Link href="/" className="relative mx-auto px-[1rem]">
                <span className="sr-only">{strings?.brand}</span>
                <Logo variant="small" theme="light" />
              </Link>
            </div>

            <div>
              <p className="font-gillsans font-light">
                <Typography variant="small">{brand}</Typography>
                <br />
                <Typography variant="small">{infos?.address}</Typography>
                <br />
                <a
                  href={links?.phone.url}
                  target={links?.phone.target}
                  className="text-label transition-colors duration-300 ease-out-cubic hover:text-copper"
                >
                  {infos?.phone}
                </a>
                <br />
                <a
                  href={links?.email.url}
                  target={links?.email.target}
                  className="text-label transition-colors duration-300 ease-out-cubic hover:text-copper"
                >
                  {infos?.email}
                </a>
                <br />
                <Typography variant="small">{infos?.piva}</Typography>
                <br />
                <Typography variant="small">{infos?.cin}</Typography>
              </p>
            </div>

            <div className="flex justify-center">
              <Socials list={menu?.social_menu} theme="light" />
            </div>

            <div className="flex justify-center">
              <ul className="flex space-x-[1.6rem]">
                {footer?.awards.map(({ link, logo }: any, index: number) => (
                  <li
                    key={`award-${index}`}
                    className="block h-[6rem] w-[6rem] rounded-full bg-transparent"
                  >
                    {link.url ? (
                      <Link
                        href={link.url}
                        target={link.target}
                        className="relative block aspect-square"
                      >
                        <span className="sr-only">{link.title}</span>
                        <Image
                          src={logo.url}
                          alt={logo.alt}
                          fill
                          loading="eager"
                          className="object-contain"
                        />
                      </Link>
                    ) : (
                      <div className="relative block aspect-square">
                        <span className="sr-only">{logo.alt}</span>
                        <Image
                          src={logo.url}
                          alt={logo.alt}
                          fill
                          loading="eager"
                          className="object-contain"
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col items-center gap-y-[0.8rem] md:flex-row md:gap-x-[3.2rem]">
              <Typography variant="small" className="font-gillsans font-light">
                {footer?.colophon.replace('[YEAR]', new Date().getFullYear())}
              </Typography>

              {menu?.legals_menu?.menu.map(({ title, url, target }, index) => (
                <Link
                  key={`legal-menu-${index}`}
                  href={url}
                  target={target}
                  className="-m-[1rem] flex p-[1rem] transition-colors duration-300 ease-out-cubic hover:text-copper"
                >
                  <Typography
                    variant="small"
                    className="font-gillsans font-light"
                  >
                    {title}
                  </Typography>
                </Link>
              ))}
            </div>
          </div>
        </Grid>
      </Container>
    </footer>
  )
}

export default Footer
